import { KeyboardEventHandler } from 'react';
import * as styles from './Table.module.css';

/**
 * Returns accessibility props that should be added to interactive table cells
 * 
 * @param {Function} onClick - The click handler function to call when Enter or Space is pressed
 * @returns {Object} An object with accessibility properties
 */
export const getAccessibleTableCellProps = (onClick: () => void) => ({
  role: "button",
  tabIndex: 0,
  className: 'focusable',
  onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClick();
      e.preventDefault();
    }
  }
});

/**
 * Returns accessibility props that should be added to clickable divs
 * 
 * @param {Function} onClick - The click handler function to call when Enter or Space is pressed
 * @param {string} ariaLabel - Accessibility label for screen readers
 * @returns {Object} An object with accessibility properties
 */
export const getAccessibleDivProps = (onClick: () => void, ariaLabel: string) => ({
  role: "button",
  tabIndex: 0,
  className: 'focusable',
  "aria-label": ariaLabel,
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClick();
      e.preventDefault();
    }
  }
});

/**
 * Returns the required props to make pagination elements accessible
 * Fixes issues with aria-labelledby and aria-describedby
 * 
 * @param {string} uniqueId - A unique identifier for this pagination component
 * @returns {Object} An object with accessibility properties
 */
export const getAccessiblePaginationProps = (uniqueId: string) => {
  const labelId = `pagination-label-${uniqueId}`;
  const selectId = `pagination-select-${uniqueId}`;
  const descriptionId = `pagination-description-${uniqueId}`;
  
  return {
    labelIds: {
      labelId,
      selectId,
      descriptionId
    },
    labelProps: {
      id: labelId,
      className: 'srOnly'
    },
    selectProps: {
      id: selectId,
      'aria-labelledby': labelId,
      'aria-describedby': descriptionId
    },
    descriptionProps: {
      id: descriptionId,
      className: 'srOnly'
    }
  };
};

/**
 * Returns the properties needed to fix aria-labelledby and aria-describedby on MUI's TablePagination
 * 
 * @param {string} tableId - A unique identifier for the table
 * @returns {Object} Object containing the inputProps to pass to TablePagination's SelectProps
 */
export const getFixedPaginationSelectProps = (tableId: string) => {
  return {
    inputProps: {
      'aria-labelledby': `pagination-label-${tableId}`,
      'aria-describedby': `pagination-description-${tableId}`,
      id: `pagination-select-${tableId}`
    }
  };
};

/**
 * Function to generate the proper HTML elements for pagination accessibility
 * This generates the HTML string that should be added to the DOM before the table
 * 
 * @param {string} tableId - A unique identifier for the table
 * @returns {string} HTML for hidden labels that pagination can reference
 */
export const getPaginationAccessibilityHTML = (tableId: string) => {
  return `
    <span id="pagination-label-${tableId}" class="srOnly">Rows per page</span>
    <span id="pagination-description-${tableId}" class="srOnly">Select number of rows to display per page</span>
  `;
};

/**
 * Enhanced accessibility props for clickable table cells with links
 * Ensures proper keyboard navigation and screen reader support
 * 
 * @param {string} href - The URL the link points to
 * @param {string} label - Descriptive label for the link
 * @param {Function} [onClick] - Optional click handler
 * @returns {Object} Accessibility properties for table cells with links
 */
export const getAccessibleLinkCellProps = (href: string, label: string, onClick?: () => void) => ({
  role: "button",
  tabIndex: -1, // Let the inner link handle focus
  className: 'focusable',
  "aria-label": label,
  onClick: onClick || (() => {
    // If no custom onClick, navigate to the href
    window.location.href = href;
  }),
  onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (onClick) {
        onClick();
      } else {
        window.location.href = href;
      }
      e.preventDefault();
    }
  }
});

/**
 * Returns accessibility props for table cells containing interactive content
 * Automatically handles role, tabindex, and keyboard navigation
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.ariaLabel - Screen reader label
 * @param {Function} [options.onClick] - Click handler
 * @param {boolean} [options.isClickable] - Whether the cell is clickable
 * @param {boolean} [options.hasInteractiveContent] - Whether cell contains interactive elements
 * @returns {Object} Accessibility properties
 */
export const getTableCellAccessibilityProps = (options: {
  ariaLabel: string;
  onClick?: () => void;
  isClickable?: boolean;
  hasInteractiveContent?: boolean;
}) => {
  const { ariaLabel, onClick, isClickable = false, hasInteractiveContent = false } = options;
  
  // Base props for all cells
  const baseProps = {
    "aria-label": ariaLabel,
  };
  
  // If cell is clickable or has interactive content, add button role and keyboard support
  if (isClickable || hasInteractiveContent) {
    return {
      ...baseProps,
      role: "button",
      tabIndex: hasInteractiveContent ? -1 : 0, // Let inner elements handle focus if they exist
      className: 'focusable',
      onClick,
      onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          onClick();
          e.preventDefault();
        }
      }
    };
  }
  
  return baseProps;
};

/**
 * Enhanced props for sort buttons to ensure proper accessibility
 * 
 * @param {string} columnName - Name of the column being sorted
 * @param {string} [sortDirection] - Current sort direction ('asc', 'desc', or undefined)
 * @returns {Object} Accessibility properties for sort buttons
 */
export const getSortButtonAccessibilityProps = (columnName: string, sortDirection?: string) => {
  const sortText = sortDirection 
    ? `sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}`
    : 'not sorted';
    
  return {
    "aria-label": `Sort ${columnName} column, currently ${sortText}`,
    role: "button",
    tabIndex: 0,
    className: 'focusable',
  };
};

/**
 * Returns proper ARIA attributes for table headers
 * 
 * @param {string} columnName - Name of the column
 * @param {boolean} [isSortable] - Whether the column is sortable
 * @param {string} [sortDirection] - Current sort direction
 * @returns {Object} ARIA attributes for table headers
 */
export const getTableHeaderAccessibilityProps = (
  columnName: string, 
  isSortable: boolean = false, 
  sortDirection?: string
) => {
  const baseProps = {
    role: "columnheader",
    "aria-label": columnName,
  };
  
  if (isSortable) {
    return {
      ...baseProps,
      "aria-sort": sortDirection === 'asc' ? 'ascending' as const : 
                   sortDirection === 'desc' ? 'descending' as const : 
                   'none' as const,
    };
  }
  
  return baseProps;
}; 